import React, { useState } from 'react';
import { MapContainer, Marker, Popup, GeoJSON, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { BasemapLayers, BasemapToggle } from './MapBasemaps';
import { API_BASE_URL } from '../config';

// Numbered blue corner pin (HTML divIcon — no external marker image needed).
function cornerIcon(n) {
  return L.divIcon({
    className: '',
    html: `<div style="background:#2563eb;color:#fff;border:2px solid #fff;border-radius:50%;
      width:26px;height:26px;display:flex;align-items:center;justify-content:center;
      font-size:13px;font-weight:700;box-shadow:0 1px 4px rgba(0,0,0,.5)">${n}</div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 13],
  });
}

// Fit the view to the boundary + all photo pins.
function FitBounds({ photos, boundary }) {
  const map = useMap();
  React.useEffect(() => {
    const pts = (photos || []).map((p) => [p.lat, p.lng]);
    if (boundary && boundary.features && boundary.features.length) {
      try {
        const b = L.geoJSON(boundary).getBounds();
        if (b.isValid()) pts.push(b.getNorthEast(), b.getSouthWest());
      } catch (e) { /* ignore bad geometry */ }
    }
    if (pts.length === 1) { map.setView(pts[0], 18); return; }
    if (pts.length > 1) map.fitBounds(L.latLngBounds(pts), { padding: [40, 40], maxZoom: 19 });
  }, [photos, boundary, map]);
  return null;
}

/**
 * Client-portal corner map: property boundary (magenta) + geotagged field photos
 * as numbered pins with photo popups, over aerial imagery (street toggle).
 * Renders nothing if there's neither a boundary nor any geotagged photo.
 */
export default function PortalCornerMap({ mapData, height = 360 }) {
  const [basemap, setBasemap] = useState('satellite');
  const photos = (mapData && mapData.photos) || [];
  const boundary = mapData && mapData.boundary;
  const hasBoundary = !!(boundary && boundary.features && boundary.features.length);
  const base = API_BASE_URL || '';

  if (!photos.length && !hasBoundary) return null;

  const center = photos.length ? [photos[0].lat, photos[0].lng] : [34.0, -84.0];

  return (
    <div style={{ height, borderRadius: '0.5rem', overflow: 'hidden', position: 'relative' }}>
      <MapContainer center={center} zoom={16} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
        <BasemapLayers basemap={basemap} />
        <FitBounds photos={photos} boundary={boundary} />
        {hasBoundary && (
          <GeoJSON data={boundary} style={{ color: '#ff00ff', weight: 3, fillColor: '#ff00ff', fillOpacity: 0.08 }} />
        )}
        {photos.map((p, i) => (
          <Marker key={p.id} position={[p.lat, p.lng]} icon={cornerIcon(i + 1)}>
            <Popup>
              <div style={{ width: 220 }}>
                <img
                  src={`${base}${p.image_url}`}
                  alt={p.caption || `Corner ${i + 1}`}
                  style={{ width: '100%', borderRadius: 6, display: 'block', marginBottom: 6 }}
                />
                {p.caption && <div style={{ fontWeight: 600, fontSize: 13 }}>{p.caption}</div>}
                <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>
                  {p.lat.toFixed(6)}, {p.lng.toFixed(6)}
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
      <BasemapToggle basemap={basemap} setBasemap={setBasemap} />
    </div>
  );
}
